import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/shared/lib/cn";
import { IconButton, useTopbarActionsRef, useTopbarLeadingRef } from "@/shared/ui";
import { Brand } from "./Brand";
import { MenuIcon } from "./icons";

export interface TopbarProps {
  /** Adds the hairline + blur once the content column has scrolled. */
  scrolled: boolean;
  /** Opens the mobile sidebar drawer. */
  onOpenMenu: () => void;
  /** Route-aware actions rendered on the right (e.g. conversation export). */
  actions?: ReactNode;
}

/**
 * The shell's single top row. Every page renders its title into the LEADING
 * slot and its primary controls into the ACTIONS slot (via the TopbarSlot
 * portals), so page headers sit on the same line as the sidebar brand instead
 * of in a second band below it.
 */
export function Topbar({ scrolled, onOpenMenu, actions }: TopbarProps) {
  const { t } = useTranslation();
  const leadingRef = useTopbarLeadingRef();
  const actionsRef = useTopbarActionsRef();

  return (
    <header
      className={cn(
        // Desktop `pt-5` centers the h-9 slot controls on the exact line of the
        // sidebar brand row (sidebar: pt-5 + a 36px row); mobile keeps py-3
        // around the hamburger + brand.
        "z-10 flex flex-none items-center gap-3 border-b border-transparent px-6 pb-3 pt-3 transition-[background,border-color] duration-200 ease-house min-[881px]:pt-5 max-[880px]:px-4",
        scrolled && "border-line bg-canvas/80 backdrop-blur-[10px]",
      )}
    >
      <IconButton
        aria-label={t("sidebar.openSidebar")}
        onClick={onOpenMenu}
        className="hidden max-[880px]:inline-grid"
      >
        <MenuIcon />
      </IconButton>

      <div className="hidden items-center max-[880px]:flex">
        <Brand />
      </div>

      {/* Leading slot — the page title / model picker portals in here. */}
      <div ref={leadingRef} className="flex min-w-0 items-center gap-3 empty:hidden" />

      <div className="flex-1" />

      {/* Actions slot — the page's primary right-side controls portal in here. */}
      <div ref={actionsRef} className="flex items-center gap-2 empty:hidden" />

      {actions ? <div className="flex items-center gap-3 empty:hidden">{actions}</div> : null}
    </header>
  );
}
